/**
 * Alarm severity/category chip — shared between the alarm history grid and
 * the active-alarms dashboard summary card.
 *
 * A site-alarms wrapper over the shared AlarmChip pill: colors from the
 * definition's severity via alarmLabel's alarmChipFamily, and appends a
 * duplicate-raise count ("Loss of Power x3") when the same alarm has fired
 * more than once.
 *
 * Contains:
 * - AlarmChip — severity/category chip (default export)
 */
import { Box, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

import BaseAlarmChip from '@sophocles/components/AlarmChip';
import { alarmChipFamily } from '@sophocles/views/sites/components/alarms/alarmLabel';

/**
 * @param {object}   props
 * @param {string}   [props.categoryKey] - top-level alarm category key, drives the fallback label
 * @param {string}   [props.label]       - translated alarm label; falls back to the category key
 * @param {Function} props.tAlarm        - alarm-namespace translator (useAlarmTranslation())
 * @param {number}   [props.count=1]     - duplicate-raise count; appended as " x{{count}}" when > 1
 * @param {string}   [props.severity]    - alarm severity, drives the chip color
 * @param {string}   [props.tooltip]     - optional hover description
 */
function AlarmChip({ categoryKey, label, tAlarm, count = 1, severity, tooltip }) {
  const resolvedLabel =
    label ?? tAlarm(`alarms.cat_${categoryKey}`, { defaultValue: (categoryKey ?? '').replace(/_/g, ' ') });
  const displayLabel = count > 1 ? `${resolvedLabel} \xd7${count}` : resolvedLabel;
  // StatusChip renders its label as a bare, non-shrinking text node; wrap it
  // here (not in the shared component) so only this alarm pill ellipsizes
  // when its column shrinks. The tooltip always carries the untruncated name
  // so a clipped pill stays fully readable on hover.
  const tooltipText = tooltip ? `${displayLabel}: ${tooltip}` : displayLabel;
  const chip = (
    <BaseAlarmChip
      label={
        <Box
          component="span"
          sx={{ display: 'block', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {displayLabel}
        </Box>
      }
      color={alarmChipFamily(severity)}
      sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}
    />
  );
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', minWidth: 0, maxWidth: '100%', overflow: 'hidden', height: '100%' }}
    >
      <Tooltip title={tooltipText}>{chip}</Tooltip>
    </Box>
  );
}

AlarmChip.propTypes = {
  categoryKey: PropTypes.string,
  label: PropTypes.string,
  tAlarm: PropTypes.func.isRequired,
  count: PropTypes.number,
  severity: PropTypes.string,
  tooltip: PropTypes.string,
};

export default AlarmChip;
